import Typography from '../StyledTypography';
import Paper from 'apollo-react/components/Paper';
import Doctor2 from 'apollo-react-icons/Doctor2';
import ChartPie from 'apollo-react-icons/ChartPie';
import Table from 'apollo-react-icons/Table';
import Route from 'apollo-react-icons/Route';
import { Link } from 'react-router-dom';
import ArrowRight from 'apollo-react-icons/ArrowRight';
import { useTranslation } from 'react-i18next';
import { convertUTCToLocal } from '../../utils/dateUtils';
import RequestTag from '../RequestTag/RequestTag';
import './styles.css';
import { STAGE_TYPES } from '../../../constants/workflow.constant';
import { REQUEST_STATUS } from '../../../constants/request.constant';
import {
  formatPriorityLabel,
  getPriorityBadgeClassName,
  isActiveWorkflowStatus,
  shouldDisplayPriorityBadge,
} from '../../../utils/priorityBadgeUtils';
import '../../../styles/priorityBadge.css';

const TrialCard = ({ request, activeTab, onNavigate, status }) => {
  const { t } = useTranslation();

  // Get current stage from request progress
  const currentRequestProgress = request?.requests_progresses?.find(
    (progress) => progress.active
  );
  const currentStage = currentRequestProgress?.StageRelation?.Child;
  const stageName = currentStage?.name || 'N/A';

  const studyName = request?.study_name;
  const generatedId = request?.generated_id;

  // Get the last action date from the most recent progress record
  // This reflects when the last action (submission, approval, etc.) was taken
  const lastActionDate =
    currentRequestProgress?.updatedAt || request?.updatedAt;

  // Build navigation URL for Link (customer side)
  const navigateTo = `/customer/progress/requests/${request.id}${
    studyName ? `?request_name=${encodeURIComponent(studyName)}` : ''
  }`;

  const isAdmin = !!onNavigate;

  const activeRequestProgress = request?.requests_progresses?.find(
    (progress) => progress.active
  );
  const remainingDays =
    request?.remainingDays ?? activeRequestProgress?.remainingDays;
  const priority = request?.priority ?? activeRequestProgress?.priority ?? null;

  const isActiveWorkflowTab = isActiveWorkflowStatus(status);

  const shouldShowPriorityBadge =
    shouldDisplayPriorityBadge({
      status,
      priority,
      hasTag: !!request?.tag,
      isActiveWorkflowTab,
    }) && Boolean(priority);

  const shouldShowStatusBadge =
    !request?.tag && !shouldShowPriorityBadge && !isActiveWorkflowTab;

  const shouldShowDays = isActiveWorkflowTab;

  const daysDisplay =
    remainingDays !== null && remainingDays !== undefined ? remainingDays : '-';

  const getStatusBadge = () => {
    if (isAdmin) {
      switch (activeTab) {
        case 0:
          return { text: t('Assigned'), className: 'submitted' };
        case 1:
          return { text: t('In Progress'), className: 'submitted' };
        case 2:
          return { text: t('Approved'), className: 'approved' };
        case 3:
          return { text: t('Rejected'), className: 'rejected' };
        default:
          return { text: t('Assigned'), className: 'submitted' };
      }
    }

    switch (activeTab) {
      case 0:
        return { text: t('Assigned'), className: 'submitted' };
      case 1:
        return {
          text:
            currentStage?.StageType?.type === STAGE_TYPES.SUBMISSION
              ? t('Under Submission')
              : t('Under Review'),
          className: 'submitted',
        };
      case 2:
        return { text: t('Approved'), className: 'approved' };
      case 3:
        return { text: t('Rejected'), className: 'rejected' };
      case 4:
        return { text: t('Draft'), className: 'draft' };
      default:
        return { text: t('Submitted'), className: 'submitted' };
    }
  };

  const statusBadge = getStatusBadge();

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return convertUTCToLocal(date, 'dd/MM/yy HH:mm');
  };

  return (
    <Paper className="trial-card">
      <div className="header-container">
        <div className="header-left-container">
          <RequestTag request={request} status={status} />

          {shouldShowPriorityBadge && (
            <span className={getPriorityBadgeClassName(priority)}>
              {t('requestBadges.priority')}: {formatPriorityLabel(priority)}
            </span>
          )}

          {shouldShowStatusBadge && (
            <span className={`status-badge ${statusBadge.className}`}>
              {statusBadge.text}
            </span>
          )}

          {shouldShowDays && (
            <span className="days-badge">
              {daysDisplay} {t('requestBadges.days')}
            </span>
          )}
        </div>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Doctor2 />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Request Name')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {studyName || 'N/A'}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <ChartPie />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Request ID')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {generatedId}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <ArrowRight />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Stage Name')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {stageName}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Table />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Submission date')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {formatDate(request.createdAt)}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Route />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Last action date')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {formatDate(lastActionDate)}
        </Typography>
      </div>

      <div className="view-details">
        {onNavigate ? (
          <button
            onClick={() => onNavigate(request.id)}
            className="link-style view-details-button"
          >
            {t('View Details')}
          </button>
        ) : (
          <Link
            to={navigateTo}
            state={{ isRequestActive: activeTab === 0 }}
            className="link-style"
          >
            {t('View Details')}
          </Link>
        )}
      </div>
    </Paper>
  );
};

export default TrialCard;
